"use strict";
import { getProtocolColor, getProtocolName } from "appGraphql/data/util";
import { useHeaderDateFormatter } from "components/Charts/hooks";
import { PROTOCOL_LEGEND_ELEMENT_ID } from "components/Charts/types";
import { EllipsisTamaguiStyle } from "theme/components/styles";
import { Flex, Text, styled } from "ui/src";
import { useLocalizationContext } from "uniswap/src/features/language/LocalizationContext";
import { NumberType } from "utilities/src/format/types";
const ProtocolLegendWrapper = styled(Flex, {
  position: "absolute",
  right: 0,
  py: "$spacing4",
  px: "$spacing12",
  gap: "$gap12",
  pointerEvents: "none",
  variants: {
    hover: {
      true: {
        right: "unset",
        p: "$spacing8",
        gap: "$gap6",
        borderRadius: "$rounded12",
        border: "1px solid",
        borderColor: "$surface3",
        backgroundColor: "$surface2",
        boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 6px 2px rgba(0, 0, 0, 0.03)",
        zIndex: "$tooltip"
      }
    }
  }
});
function ProtocolLegend({ protocolData }) {
  const { convertFiatAmountFormatted } = useLocalizationContext();
  return <ProtocolLegendWrapper id={PROTOCOL_LEGEND_ELEMENT_ID} hover={true}>{protocolData?.map(({ value, protocol }) => {
    const display = value ? convertFiatAmountFormatted(value, NumberType.FiatTokenStats) : null;
    return !!display && <Flex row gap={8} justifyContent="flex-end" key={protocol + "_blip"} width="max-content"><Text variant="body4" textAlign="right" color="$neutral2" lineHeight={12}>{getProtocolName(protocol)}</Text><Flex borderRadius="$rounded4" width={12} height={12} backgroundColor={getProtocolColor(protocol)} /><Text variant="body4" textAlign="right" lineHeight={12} {...EllipsisTamaguiStyle}>{display}</Text></Flex>;
  }).reverse()}</ProtocolLegendWrapper>;
}
function formatUSDCompact(value) {
  if (value === null || value === void 0 || isNaN(value))
    return "$0.00";
  const abs = Math.abs(value);
  let formatted;
  if (abs >= 1e9) {
    formatted = (value / 1e9).toFixed(2) + "B";
  } else if (abs >= 1e6) {
    formatted = (value / 1e6).toFixed(2) + "M";
  } else if (abs >= 1e3) {
    formatted = (value / 1e3).toFixed(2) + "K";
  } else {
    formatted = value.toFixed(2);
  }
  return `$${formatted}`;
}
function HeaderValueDisplay({
  value,
  valueFormatterType = NumberType.FiatTokenStats,
  symbol,
  fdv,
  vol
}) {
  const { convertFiatAmountFormatted } = useLocalizationContext();
  if (typeof value !== "number" && typeof value !== "undefined") {
    return <>{value}</>;
  }
  return <div style={{ display: "flex", justifyContent: "space-between" }}><div style={{ display: "flex", alignItems: "center", gap: "10px" }}><div style={{ width: "40px" }}><img src={symbol.logo} alt="" style={{ width: "100%", objectFit: "cover" }} /></div><p style={{ fontSize: "20px" }}>{symbol.symbol} - </p><Text variant="heading3" {...EllipsisTamaguiStyle}>{convertFiatAmountFormatted(value, valueFormatterType)}</Text></div>{fdv && vol && <div style={{ display: "flex", gap: "20px", marginRight: "-100px" }}><div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}><p style={{ fontSize: "16px", opacity: 0.7 }}>FDV</p><p style={{ fontSize: "18px", marginTop: "-16px" }}>{formatUSDCompact(fdv)}</p></div><div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}><p style={{ fontSize: "16px", opacity: 0.7 }}>24H VOL</p><p style={{ fontSize: "18px", marginTop: "-16px" }}>{formatUSDCompact(vol)}</p></div></div>}</div>;
}
function HeaderTimeDisplay({ time, timePlaceholder }) {
  const headerDateFormatter = useHeaderDateFormatter();
  return <Text variant="subheading2" display="flex" alignItems="center" color="neutral2">{time ? headerDateFormatter(time) : timePlaceholder}</Text>;
}
export function ChartHeader({
  value,
  valueFormatterType,
  time,
  timePlaceholder,
  protocolData,
  additionalFields,
  symbol,
  fdv,
  vol
}) {
  const isHovered = !!time;
  return <Flex
    row
    position="absolute"
    width="100%"
    gap="$gap8"
    alignItems="flex-start"
    animation="fast"
    zIndex="$default"
    id="chart-header"
  ><Flex position="absolute" gap="$gap4" pb={14} pointerEvents="none" width="80%"><HeaderValueDisplay fdv={fdv} vol={vol} symbol={symbol} value={value} valueFormatterType={valueFormatterType} /><Flex row gap="$gap8" {...EllipsisTamaguiStyle}>{additionalFields}<HeaderTimeDisplay time={time} timePlaceholder={timePlaceholder} /></Flex></Flex>{isHovered && protocolData && <ProtocolLegend protocolData={protocolData} />}</Flex>;
}
